import * as THREE from "three";
import { FontLoader, Font } from "three/examples/jsm/loaders/FontLoader.js";
import { TextGeometry } from "three/examples/jsm/geometries/TextGeometry.js";
import { ParticleSystem } from "./particles";
import { createCollisionUniforms, CollisionUniforms } from "./objects";
import { Emitter } from "./emitters";
import { FIXED_DT, VIEW_HEIGHT } from "./constants";

const WORD = "Resonella";

interface FontOption {
  label: string;
  url: string;
}

const FONTS: FontOption[] = [
  { label: "Helvetiker Bold", url: "fonts/helvetiker_bold.typeface.json" },
  { label: "Helvetiker", url: "fonts/helvetiker_regular.typeface.json" },
  { label: "Optimer Bold", url: "fonts/optimer_bold.typeface.json" },
  { label: "Gentilis Bold", url: "fonts/gentilis_bold.typeface.json" },
  { label: "Droid Sans Bold", url: "fonts/droid_sans_bold.typeface.json" },
  { label: "Droid Serif Bold", url: "fonts/droid_serif_bold.typeface.json" },
  { label: "Righteous", url: "fonts/Righteous-Regular.typeface.json" },
];

/**
 * Master gate for the ?title DEV trajectory tuner (live panel, collider
 * wireframes, continuous emit). Sealed off (false) now that the trajectory is
 * baked; the tuner implementation is kept intact — flip to true to re-tune.
 */
const TUNER_ENABLED = false;

const TEXT_COLOR = 0xeaf6ff;
const BG_COLOR = 0x0a0a0f;
const PARTICLE_COLOR = new THREE.Color(0x8fd3ff);

/** TextGeometry curve subdivisions; high enough that letter curves read smooth, not faceted, at logo size. */
const CURVE_SEGMENTS = 24;

/**
 * Best-effort audio surface handed to the title from main() (`attachTitleAudio`),
 * which runs concurrently: the editor's AudioEngine is created after the title
 * has already started, so this is a "delivered later" receiver. `resume()` is a
 * best-effort autoplay-unlock; `playTitleBell` routes a letter hit through the
 * worklet voice pool.
 */
export interface TitleAudioSink {
  resume(): void;
  titleAudioReady(): boolean;
  playTitleBell(pitchIndex: number, velocity: number): void;
}

let titleActive = false;
let titleAudio: TitleAudioSink | null = null;

/**
 * True while the title overlay owns the frame — from `runTitleScreen` start
 * until the fade begins (or false immediately when the title is suppressed).
 * The editor frame loop reads this to skip its particle sim + contact audio so
 * the scene does not advance underneath the title (it starts from its initial
 * state when the fade reveals it). The fade START is the flip point so the
 * scene begins moving as the white background dissolves.
 */
export function isTitleActive(): boolean {
  return titleActive;
}

/**
 * Hands the editor's AudioEngine to the running title. Called from main() right
 * after the engine is constructed; the title may already be animating, so this
 * also fires a best-effort resume in case a user gesture already unlocked audio.
 */
export function attachTitleAudio(engine: TitleAudioSink): void {
  titleAudio = engine;
  if (titleActive) {
    engine.resume();
  }
}

/** Bell pitch index per hit letter (minor-pentatonic ascent R→o→e→l→l). */
const BELL_PITCH_BY_LETTER: Record<number, number> = { 0: 0, 3: 3, 5: 5, 6: 7, 7: 10 };
const BELL_VELOCITY = 0.55;

/**
 * Max time the sim waits for audio readiness (context running + bell worklet
 * node) after the click-to-play gesture before starting anyway, so a broken or
 * disabled worklet (?worklet=0) cannot freeze the title.
 */
const AUDIO_READY_WAIT_MAX_MS = 2000;

/** Click-to-play prompt: world-unit glyph size / vertical center y / peak opacity / blink period / fade-out after click. */
const PROMPT_TEXT_SIZE = 0.33;
const PROMPT_Y = -3.6;
const PROMPT_OPACITY = 0.4;
const PROMPT_BLINK_PERIOD_MS = 2000;
const PROMPT_FADE_MS = 300;

type HitShape = "box" | "sphere";

interface HitSpec {
  index: number;
  shape: HitShape;
  offsetXFrac?: number;
}

const HIT_SPECS: HitSpec[] = [
  { index: 0, shape: "box" },
  { index: 3, shape: "sphere" },
  { index: 5, shape: "sphere" },
  { index: 6, shape: "box" },
  { index: 7, shape: "box" },
];

interface TitleParams {
  textSize: number;
  targetWidth: number;
  letterSpacing: number;
  gravityY: number;
  emitSpeed: number;
  particleLife: number;
  emitterX: number;
  emitterY: number;
  headingDeg: number;
  restitution: number;
  friction: number;
  pointSize: number;
  clockPeriod: number;
  seqNum: number;
  pulsePeak: number;
  pulseTau: number;
  oOffsetX: number;
  simDurationS: number;
  textFadeS: number;
  fadeStartS: number;
  fadeMs: number;
}

const P: TitleParams = {
  textSize: 3.2,
  targetWidth: 6,
  letterSpacing: 0.064, // 0.02em × textSize 3.2 (mirrors .landing-logo letter-spacing)
  gravityY: -23.5,
  emitSpeed: 6.4,
  particleLife: 4.0,
  emitterX: -4.55,
  emitterY: 1,
  headingDeg: 55,
  restitution: 1,
  friction: 0,
  pointSize: 0.08,
  clockPeriod: 0.5,
  seqNum: 16,
  pulsePeak: 0.0375,
  pulseTau: 0.11,
  oOffsetX: 0.02,
  simDurationS: 2.1,
  textFadeS: 0.3,
  fadeStartS: 2.6,
  fadeMs: 300,
};

type NumKey =
  | "textSize" | "targetWidth" | "letterSpacing" | "gravityY" | "emitSpeed"
  | "particleLife" | "emitterX" | "emitterY" | "headingDeg" | "restitution"
  | "friction" | "pointSize" | "clockPeriod" | "seqNum" | "pulsePeak"
  | "pulseTau" | "oOffsetX" | "simDurationS" | "textFadeS" | "fadeStartS" | "fadeMs";

interface SliderSpec {
  key: NumKey;
  label: string;
  min: number;
  max: number;
  step: number;
}

const SLIDERS: SliderSpec[] = [
  { key: "targetWidth", label: "title size", min: 6, max: 30, step: 0.5 },
  { key: "gravityY", label: "gravity Y", min: -40, max: 0, step: 0.5 },
  { key: "emitterX", label: "emitter X", min: -20, max: 20, step: 0.25 },
  { key: "emitterY", label: "emitter Y", min: -12, max: 12, step: 0.25 },
  { key: "headingDeg", label: "heading °", min: -180, max: 180, step: 1 },
  { key: "emitSpeed", label: "speed", min: 0, max: 40, step: 0.5 },
  { key: "restitution", label: "bounce", min: 0, max: 1.2, step: 0.02 },
  { key: "friction", label: "friction", min: 0, max: 1, step: 0.01 },
  { key: "clockPeriod", label: "seq period", min: 0.05, max: 1.5, step: 0.01 },
  { key: "seqNum", label: "seq cells", min: 1, max: 48, step: 1 },
  { key: "particleLife", label: "life", min: 0.5, max: 8, step: 0.1 },
  { key: "pointSize", label: "point size", min: 0.05, max: 1.5, step: 0.01 },
  { key: "pulsePeak", label: "pulse peak", min: 0, max: 0.5, step: 0.005 },
  { key: "pulseTau", label: "pulse tau", min: 0.02, max: 0.5, step: 0.01 },
  { key: "oOffsetX", label: "o offset X", min: -1, max: 1, step: 0.01 },
  { key: "simDurationS", label: "title dur", min: 0.5, max: 8, step: 0.1 },
  { key: "textFadeS", label: "text fade s", min: 0.05, max: 3, step: 0.05 },
  { key: "fadeStartS", label: "fade start", min: 0.5, max: 8, step: 0.1 },
  { key: "fadeMs", label: "bg fade ms", min: 50, max: 2000, step: 50 },
];

interface HitBox {
  letterIndex: number;
  shape: HitShape;
  center: THREE.Vector3;
  half: THREE.Vector3;
  radius: number;
}

interface LetterMesh {
  mesh: THREE.Mesh;
  baseScale: number;
}

const fontCache = new Map<string, Font>();
let currentFontUrl = "fonts/Righteous-Regular.typeface.json";

/**
 * Loads a typeface.json font, memoising per URL so switching back to an
 * already-loaded font in the tuner is instant and never re-fetches. To audition
 * a .ttf, convert it to typeface.json offline (three's TTFLoader convert logic)
 * and add the result to FONTS.
 */
async function loadFont(url: string): Promise<Font> {
  const cached = fontCache.get(url);
  if (cached) {
    return cached;
  }
  const font = await new FontLoader().loadAsync(url);
  fontCache.set(url, font);
  return font;
}

/**
 * Warms the title font cache while the landing is on screen, so the title
 * animation's first frame renders letters immediately on Play (no async font
 * gap) and the landing → title handoff stays continuous.
 */
export function preloadTitleFont(): void {
  void loadFont(currentFontUrl);
}

/**
 * Fixed 24-float A..F column layout of one collider row in uObjTex, mirroring
 * ObjectField.writeColliderSlot (src/objects.ts). The title packs its colliders
 * directly rather than instantiating an ObjectField. objA.w is the type code
 * (0 = box → objB.xyz half-extents; 1 = sphere → objB.x radius). objD.w
 * (collisionEnabled) must be 1 or evalObject rejects the slot.
 */
function writeColliderSlot(tex: Float32Array, k: number, box: HitBox): void {
  const base = k * 24;
  const type = box.shape === "sphere" ? 1 : 0;
  tex[base] = box.center.x; tex[base + 1] = box.center.y; tex[base + 2] = box.center.z; tex[base + 3] = type;
  if (type === 1) {
    tex[base + 4] = box.radius; tex[base + 5] = 0; tex[base + 6] = 0; tex[base + 7] = 0;
  } else {
    tex[base + 4] = box.half.x; tex[base + 5] = box.half.y; tex[base + 6] = box.half.z; tex[base + 7] = 0;
  }
  tex[base + 8] = P.restitution; tex[base + 9] = 0; tex[base + 10] = 0; tex[base + 11] = 0;
  tex[base + 12] = P.friction; tex[base + 13] = 0; tex[base + 14] = 0; tex[base + 15] = 1;
  tex[base + 16] = 0; tex[base + 17] = 0; tex[base + 18] = 0; tex[base + 19] = 1;
  tex[base + 20] = 0; tex[base + 21] = 0; tex[base + 22] = 0; tex[base + 23] = 0;
}

/**
 * A spread-0 sequencer emitter: coneDeg 0 (identical launch every particle) with
 * clock-synced sequencer emission. Only cell 0 is active. In production num is
 * P.seqNum, sized so the loop period num*cellSec exceeds the sim duration → a
 * single particle along one deterministic path. When looping (the ?title tuner),
 * num is a small constant so cell 0 re-fires every few cells, streaming the same
 * deterministic trajectory continuously while the user dials in parameters.
 */
const TUNE_LOOP_CELLS = 4;

function buildEmitter(loop: boolean): Emitter {
  const heading = (P.headingDeg * Math.PI) / 180;
  const rotation = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 0, 1), heading - Math.PI / 2);
  const num = loop ? TUNE_LOOP_CELLS : Math.max(1, Math.round(P.seqNum));
  const seqSteps = [0];
  return {
    id: "title-emitter",
    position: new THREE.Vector3(P.emitterX, P.emitterY, 0),
    rate: 0,
    rotation,
    coneDeg: 0,
    speedMin: P.emitSpeed,
    speedMax: P.emitSpeed,
    lifeMin: P.particleLife,
    lifeMax: P.particleLife,
    offset: 0,
    bounce: 1,
    friction: P.friction,
    drag: 0,
    clockSync: true,
    seqMode: true,
    meter: 4,
    num,
    seqSteps,
    kind: "sync",
    pulseSpeedFromSettings: true,
    gate: 1,
    name: "",
  };
}

/**
 * Lays out the word as one mesh per letter (each needs its own scale for the
 * hit pulse) on a shared baseline, then shifts the whole word so its advance
 * box is centered on the origin in the z=0 plane. The cursor advances by the
 * font's per-glyph advance width (`ha`), not the ink width — the landing logo
 * is CSS-rendered text with native advances, so ink-width layout would pinch
 * thin glyphs like the double l. Returns the meshes and each letter's true
 * world-space glyph AABB — the colliders derive from these, so a hit box
 * coincides with the visible glyph (uppercase full height, lowercase x-height)
 * rather than the per-letter em rectangle.
 */
function buildLetters(font: Font, scene: THREE.Scene): { letters: LetterMesh[]; bounds: (THREE.Box3 | null)[] } {
  const material = new THREE.MeshBasicMaterial({ color: TEXT_COLOR, transparent: true });
  const geos: TextGeometry[] = [];
  const advances: number[] = [];
  const minYs: number[] = [];
  const maxYs: number[] = [];
  for (const ch of WORD) {
    const geo = new TextGeometry(ch, { font, size: P.textSize, depth: 0.01, curveSegments: CURVE_SEGMENTS, bevelEnabled: false });
    geo.computeBoundingBox();
    const bb = geo.boundingBox;
    geos.push(geo);
    advances.push(font.data.glyphs[ch].ha * (P.textSize / font.data.resolution));
    minYs.push(bb ? bb.min.y : 0);
    maxYs.push(bb ? bb.max.y : P.textSize);
  }
  const totalRaw = advances.reduce((a: number, w: number): number => a + w, 0) + P.letterSpacing * (WORD.length - 1);
  const scaleFit = P.targetWidth / totalRaw;
  const totalFit = totalRaw * scaleFit;
  const wordMinY = Math.min(...minYs);
  const wordMaxY = Math.max(...maxYs);
  const baselineY = -((wordMinY + wordMaxY) / 2) * scaleFit;

  const letters: LetterMesh[] = [];
  const bounds: (THREE.Box3 | null)[] = [];
  let cursor = -totalFit / 2;
  for (let i = 0; i < WORD.length; i++) {
    const geo = geos[i];
    const bb = geo.boundingBox;
    const mesh = new THREE.Mesh(geo, material);
    mesh.scale.setScalar(scaleFit);
    mesh.position.x = cursor;
    mesh.position.y = baselineY;
    scene.add(mesh);
    letters.push({ mesh, baseScale: scaleFit });
    const inkMinX = bb ? bb.min.x : 0;
    const inkMaxX = bb ? bb.max.x : advances[i];
    const world = new THREE.Box3(
      new THREE.Vector3(cursor + inkMinX * scaleFit, baselineY + minYs[i] * scaleFit, -0.5),
      new THREE.Vector3(cursor + inkMaxX * scaleFit, baselineY + maxYs[i] * scaleFit, 0.5),
    );
    bounds.push(world);
    cursor += (advances[i] + P.letterSpacing) * scaleFit;
  }
  return { letters, bounds };
}

/**
 * Derives the hit colliders from the laid-out letter bounds per HIT_SPECS: R and
 * the two l's are boxes coinciding with their glyphs; o is a sphere (a circle in
 * the z=0 plane) whose radius is the mean glyph half-extent so it sits inside the
 * round letter. The box z half-extent / sphere radius keep the z-locked (z=0)
 * stream inside the collider's depth.
 */
function buildHitBoxes(bounds: (THREE.Box3 | null)[]): HitBox[] {
  const boxes: HitBox[] = [];
  for (const spec of HIT_SPECS) {
    const b = bounds[spec.index];
    if (!b) {
      continue;
    }
    const center = new THREE.Vector3();
    b.getCenter(center);
    const size = new THREE.Vector3();
    b.getSize(size);
    const offsetXFrac = spec.index === 3 ? P.oOffsetX : (spec.offsetXFrac ?? 0);
    center.x += offsetXFrac * size.x;
    const half = new THREE.Vector3(size.x / 2, size.y / 2, 2);
    const radius = (size.x / 2 + size.y / 2) / 2;
    boxes.push({ letterIndex: spec.index, shape: spec.shape, center, half, radius });
  }
  return boxes;
}

function packBoxes(uniforms: CollisionUniforms, boxes: HitBox[]): void {
  const tex = uniforms.uObjTex.value.image.data as Float32Array;
  for (let k = 0; k < boxes.length; k++) {
    writeColliderSlot(tex, k, boxes[k]);
  }
  uniforms.uObjTex.value.needsUpdate = true;
  uniforms.uObjCount.value = boxes.length;
  uniforms.uAlwaysCount.value = boxes.length;
  uniforms.uUseGrid.value = 0;
}

interface TitleDebug {
  P: TitleParams;
  replay: () => void;
  audioReady: () => boolean | null;
  sample: () => { x: number; y: number }[];
  boxes: () => { letterIndex: number; shape: HitShape; cx: number; cy: number; hx: number; hy: number; radius: number }[];
}

declare global {
  interface Window {
    __title?: TitleDebug;
  }
}

/**
 * The startup title sequence: a white full-screen overlay with the black word
 * "Resonella" and a deterministic single gravity-driven particle (spread-0
 * sequencer) that bounces off the letter colliders, pulsing each letter it hits.
 * Runs a fixed-step (FIXED_DT accumulator) sim so the trajectory is identical on
 * every load. The particle animates until fadeStartS; the black text fades to
 * transparent over [simDurationS, fadeStartS]; then the white background fades
 * out at fadeStartS. Resolves at fade START (so gated followers like the
 * tutorial begin as the white background dissolves, in sync with the scene sim);
 * GPU resources are disposed after the fade completes.
 *
 * ?title (DEV) runs the sim indefinitely with no fade, looping the emitter so the
 * deterministic trajectory streams continuously, and mounts a live panel (font
 * dropdown + title size / gravity / emitter / bounce / sequencer sliders) plus the
 * red collider wireframes for trajectory tuning; every edit replays the sim.
 */
export function runTitleScreen(opts: { immediate?: boolean; onReady?: () => void } = {}): Promise<void> {
  const devTune = TUNER_ENABLED && import.meta.env.DEV && new URLSearchParams(location.search).has("title");
  const immediate = opts.immediate === true;
  titleActive = true;
  titleAudio?.resume();
  return new Promise<void>((resolve: () => void): void => {
    const canvas = document.createElement("canvas");
    canvas.style.cssText =
      "position:fixed;inset:0;width:100%;height:100%;z-index:9999;" +
      "background-color:#0a0a0f;background-image:radial-gradient(circle at 50% 35%,rgba(143,211,255,0.12),transparent 60%);" +
      "opacity:1;transition:opacity " + P.fadeMs + "ms ease-out;pointer-events:none;";
    document.body.appendChild(canvas);

    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio * 2, 3));
    renderer.setClearColor(BG_COLOR, 0);
    renderer.outputColorSpace = THREE.SRGBColorSpace;

    let scene = new THREE.Scene();
    const halfH = VIEW_HEIGHT / 2;
    const camera = new THREE.OrthographicCamera(-1, 1, halfH, -halfH, 0.1, 100);
    camera.position.set(0, 0, 20);
    camera.lookAt(0, 0, 0);

    let particles: ParticleSystem | null = null;
    let letters: LetterMesh[] = [];
    let impulse = new Float32Array(WORD.length);
    let boxes: HitBox[] = [];
    let disposed = false;
    let fading = false;
    let rafId = 0;
    let lastTime = 0;
    let simTime = 0;
    let started = false;
    let readyFired = false;
    let awaitingStart = !devTune && !immediate;
    let clickedAt = 0;
    let promptMesh: THREE.Mesh | null = null;
    let promptMaterial: THREE.MeshBasicMaterial | null = null;

    const applyCamera = (): void => {
      const a = window.innerWidth / Math.max(1, window.innerHeight);
      camera.left = -halfH * a;
      camera.right = halfH * a;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight, false);
      particles?.updatePointScale();
    };

    const teardownSim = (): void => {
      particles?.dispose();
      particles = null;
      promptMesh?.geometry.dispose();
      promptMaterial?.dispose();
      promptMesh = null;
      promptMaterial = null;
      for (const l of letters) {
        l.mesh.geometry.dispose();
      }
      if (letters.length > 0) {
        (letters[0].mesh.material as THREE.Material).dispose();
      }
      letters = [];
      scene = new THREE.Scene();
    };

    const dispose = (): void => {
      if (disposed) {
        return;
      }
      disposed = true;
      titleActive = false;
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", applyCamera);
      window.removeEventListener("pointerdown", resumeAudio);
      teardownSim();
      renderer.dispose();
      canvas.remove();
      resolve();
    };

    const beginFade = (): void => {
      if (fading || devTune) {
        return;
      }
      fading = true;
      titleActive = false;
      canvas.style.opacity = "0";
      resolve();
      window.setTimeout(dispose, P.fadeMs + 50);
    };

    const buildPrompt = (font: Font): void => {
      const text = matchMedia("(pointer: coarse)").matches ? "- tap to play -" : "- click to play -";
      const geo = new TextGeometry(text, { font, size: PROMPT_TEXT_SIZE, depth: 0.01, curveSegments: CURVE_SEGMENTS, bevelEnabled: false });
      geo.computeBoundingBox();
      const bb = geo.boundingBox;
      if (bb === null) {
        return;
      }
      promptMaterial = new THREE.MeshBasicMaterial({ color: TEXT_COLOR, transparent: true, opacity: PROMPT_OPACITY });
      promptMesh = new THREE.Mesh(geo, promptMaterial);
      promptMesh.position.set(-(bb.min.x + bb.max.x) / 2, PROMPT_Y - (bb.min.y + bb.max.y) / 2, 0);
      scene.add(promptMesh);
    };

    const resumeAudio = (): void => {
      titleAudio?.resume();
      if (clickedAt === 0) {
        clickedAt = performance.now();
      }
    };

    const buildSim = (font: Font): void => {
      const built = buildLetters(font, scene);
      letters = built.letters;
      impulse = new Float32Array(WORD.length);
      boxes = buildHitBoxes(built.bounds);

      if (devTune) {
        for (const box of boxes) {
          const mat = new THREE.LineBasicMaterial({ color: 0xff3366 });
          let wire: THREE.Object3D;
          if (box.shape === "sphere") {
            const segs = 48;
            const pts: THREE.Vector3[] = [];
            for (let s = 0; s < segs; s++) {
              const a = (s / segs) * Math.PI * 2;
              pts.push(new THREE.Vector3(Math.cos(a) * box.radius, Math.sin(a) * box.radius, 0));
            }
            wire = new THREE.LineLoop(new THREE.BufferGeometry().setFromPoints(pts), mat);
          } else {
            wire = new THREE.LineSegments(new THREE.EdgesGeometry(new THREE.BoxGeometry(box.half.x * 2, box.half.y * 2, 0.1)), mat);
          }
          wire.position.copy(box.center);
          scene.add(wire);
        }
      }

      const uniforms = createCollisionUniforms();
      packBoxes(uniforms, boxes);
      particles = new ParticleSystem(renderer, uniforms);
      particles.setZLock(1);
      particles.setGravityY(P.gravityY);
      particles.setPointSize(P.pointSize);
      particles.setClockPeriod(P.clockPeriod);
      particles.setEmitters([buildEmitter(devTune)]);
      particles.setFlatColor(PARTICLE_COLOR);
      scene.add(particles.points);
      if (!devTune && !immediate) {
        buildPrompt(font);
      }
      applyCamera();
      simTime = 0;
      lastTime = 0;
      started = true;
    };

    const replay = (): void => {
      teardownSim();
      started = false;
      const font = fontCache.get(currentFontUrl);
      if (font) {
        buildSim(font);
      }
    };

    const switchFont = (url: string): void => {
      loadFont(url).then((font: Font): void => {
        currentFontUrl = url;
        teardownSim();
        started = false;
        buildSim(font);
      });
    };

    const frame = (now: number): void => {
      if (disposed) {
        return;
      }
      rafId = requestAnimationFrame(frame);
      if (!started) {
        return;
      }
      if (promptMaterial !== null) {
        promptMaterial.opacity = clickedAt === 0
          ? PROMPT_OPACITY * (0.5 + 0.5 * Math.sin((now / PROMPT_BLINK_PERIOD_MS) * Math.PI * 2))
          : Math.min(promptMaterial.opacity, Math.max(0, PROMPT_OPACITY * (1 - (now - clickedAt) / PROMPT_FADE_MS)));
      }
      if (awaitingStart) {
        const ready = titleAudio !== null && titleAudio.titleAudioReady();
        if (clickedAt !== 0 && (ready || now >= clickedAt + AUDIO_READY_WAIT_MAX_MS)) {
          awaitingStart = false;
        } else {
          renderer.render(scene, camera);
          return;
        }
      }
      const dt = lastTime === 0 ? FIXED_DT : Math.min((now - lastTime) / 1000, 0.05);
      lastTime = now;
      simTime += dt;

      if (particles && (devTune || simTime < P.fadeStartS)) {
        particles.update(dt);
        const { counts } = particles.readContactCounts();
        const decay = Math.exp(-dt / P.pulseTau);
        for (let i = 0; i < impulse.length; i++) {
          impulse[i] *= decay;
        }
        for (let slot = 0; slot < boxes.length; slot++) {
          if (counts[slot] > 0) {
            const letterIndex = boxes[slot].letterIndex;
            impulse[letterIndex] = Math.min(1, impulse[letterIndex] + 1);
            const pitch = BELL_PITCH_BY_LETTER[letterIndex];
            if (pitch !== undefined) {
              titleAudio?.playTitleBell(pitch, BELL_VELOCITY);
            }
          }
        }
        for (let i = 0; i < letters.length; i++) {
          letters[i].mesh.scale.setScalar(letters[i].baseScale * (1 + impulse[i] * P.pulsePeak));
        }
      }

      if (!devTune) {
        const span = Math.max(1e-3, P.textFadeS);
        const textFade = Math.min(1, Math.max(0, (simTime - P.simDurationS) / span));
        if (letters.length > 0) {
          (letters[0].mesh.material as THREE.MeshBasicMaterial).opacity = 1 - textFade;
        }
        particles?.setOpacity(1 - textFade);
      }

      renderer.render(scene, camera);

      if (!readyFired && letters.length > 0) {
        readyFired = true;
        opts.onReady?.();
      }

      if (!devTune && simTime >= P.fadeStartS) {
        beginFade();
      }
    };

    if (import.meta.env.DEV) {
      window.__title = {
        P,
        replay,
        audioReady: (): boolean | null => titleAudio === null ? null : titleAudio.titleAudioReady(),
        sample: (): { x: number; y: number }[] => particles ? particles.debugLivePositions() : [],
        boxes: (): { letterIndex: number; shape: HitShape; cx: number; cy: number; hx: number; hy: number; radius: number }[] =>
          boxes.map((b: HitBox): { letterIndex: number; shape: HitShape; cx: number; cy: number; hx: number; hy: number; radius: number } => ({
            letterIndex: b.letterIndex, shape: b.shape, cx: b.center.x, cy: b.center.y, hx: b.half.x, hy: b.half.y, radius: b.radius,
          })),
      };
    }

    if (devTune) {
      mountTunePanel(replay, switchFont);
    }

    window.addEventListener("resize", applyCamera);
    window.addEventListener("pointerdown", resumeAudio);
    renderer.setSize(window.innerWidth, window.innerHeight, false);
    rafId = requestAnimationFrame(frame);

    loadFont(currentFontUrl).then(buildSim).catch((): void => {
      dispose();
    });
  });
}

/**
 * DEV-only tuning overlay: one labelled slider + numeric readout per SLIDERS
 * entry, bound to the shared P params. Every edit replays the deterministic sim
 * so the user dials in the trajectory live. A Replay button re-runs without a
 * param change.
 */
function mountTunePanel(replay: () => void, switchFont: (url: string) => void): void {
  const panel = document.createElement("div");
  panel.style.cssText =
    "position:fixed;top:8px;right:8px;z-index:10000;background:rgba(255,255,255,0.92);" +
    "border:1px solid #ccc;border-radius:6px;padding:8px 10px;font:11px/1.5 monospace;" +
    "color:#111;max-height:96vh;overflow:auto;pointer-events:auto;width:620px;";
  const title = document.createElement("div");
  title.textContent = "title tuner (?title)";
  title.style.cssText = "font-weight:bold;margin-bottom:6px;";
  panel.appendChild(title);

  const fontRow = document.createElement("label");
  fontRow.style.cssText = "display:flex;align-items:center;gap:6px;margin:2px 0 8px;";
  const fontName = document.createElement("span");
  fontName.textContent = "font";
  fontName.style.cssText = "flex:0 0 74px;";
  const fontSelect = document.createElement("select");
  fontSelect.style.cssText = "flex:1 1 auto;font:inherit;";
  for (const opt of FONTS) {
    const o = document.createElement("option");
    o.value = opt.url;
    o.textContent = opt.label;
    fontSelect.appendChild(o);
  }
  fontSelect.value = currentFontUrl;
  fontSelect.addEventListener("change", (): void => switchFont(fontSelect.value));
  fontRow.appendChild(fontName);
  fontRow.appendChild(fontSelect);
  panel.appendChild(fontRow);

  for (const spec of SLIDERS) {
    const row = document.createElement("label");
    row.style.cssText = "display:flex;align-items:center;gap:6px;margin:2px 0;";
    const name = document.createElement("span");
    name.textContent = spec.label;
    name.style.cssText = "flex:0 0 74px;";
    const input = document.createElement("input");
    input.type = "range";
    input.min = String(spec.min);
    input.max = String(spec.max);
    input.step = String(spec.step);
    input.value = String(P[spec.key]);
    input.style.cssText = "flex:0 0 400px;";
    const num = document.createElement("input");
    num.type = "number";
    num.step = "any";
    num.value = String(P[spec.key]);
    num.style.cssText = "flex:0 0 60px;text-align:right;font:inherit;";
    input.addEventListener("input", (): void => {
      P[spec.key] = Number(input.value);
      num.value = input.value;
      replay();
    });
    num.addEventListener("change", (): void => {
      const v = Number(num.value);
      if (!Number.isFinite(v)) {
        return;
      }
      P[spec.key] = v;
      input.value = String(v);
      replay();
    });
    row.appendChild(name);
    row.appendChild(input);
    row.appendChild(num);
    panel.appendChild(row);
  }

  const btn = document.createElement("button");
  btn.textContent = "replay";
  btn.style.cssText = "margin-top:6px;width:100%;cursor:pointer;";
  btn.addEventListener("click", (): void => replay());
  panel.appendChild(btn);

  const dump = document.createElement("button");
  dump.textContent = "log P";
  dump.style.cssText = "margin-top:4px;width:100%;cursor:pointer;";
  dump.addEventListener("click", (): void => console.log(JSON.stringify(P, null, 2)));
  panel.appendChild(dump);

  document.body.appendChild(panel);
}
